import React, { useMemo, useState } from 'react'

import { Typography, Table, Tooltip, Tag, Select } from 'antd'
import {
	InfoCircleOutlined,
	ExclamationCircleOutlined
} from '@ant-design/icons'
import { isMobile } from 'react-device-detect'

import { useStores } from '../../../cls/stores/MobXProvider'
import { observer } from 'mobx-react-lite'
import SubscriptionActions from './SubscriptionActions'
import { useSmartTranslation } from '../../../cls/TranslationContext'

const { Title } = Typography

const ActiveSubscriptions = () => {
	const { users, subscriptions } = useStores()
	const { t } = useSmartTranslation()
	const plans = subscriptions.plans

	const { loggedInUserId } = users
	const user = users.requireUserById(loggedInUserId)
	const userSubscriptions = user.temporarySubscriptions

	const [statusFilter, setStatusFilter] = useState('all')

	const filteredSubscriptions = useMemo(() => {
		return statusFilter === 'all'
			? userSubscriptions
			: statusFilter === 'active'
			? userSubscriptions.filter(
					(subscription) =>
						subscription.status === 'active' ||
						(subscription.status === 'trialing' &&
							(subscription.items?.data[0]?.price?.nickname ||
								subscription.items?.data[0]?.price?.product
									?.name))
			  )
			: userSubscriptions.filter(
					(subscription) =>
						subscription.status === statusFilter &&
						(subscription.items?.data[0]?.price?.nickname ||
							subscription.items?.data[0]?.price?.product?.name)
			  )
	}, [userSubscriptions, statusFilter])

	const statusOptions = [
		{ value: 'all', label: t('single.status-all') },
		{ value: 'active', label: t('single.active') },
		{ value: 'canceled', label: t('single.canceled') },
		{ value: 'incomplete', label: t('single.incomplete') },
		{ value: 'past_due', label: t('single.past-due') },
		{ value: 'unpaid', label: t('single.unpaid') }
	]

	const formatPrice = (amount, currency) => {
		const formattedAmount = (amount / 100).toFixed(2)
		return formattedAmount.endsWith('.00')
			? `$${formattedAmount.slice(0, -3)} ${currency.toUpperCase()}`
			: `$${formattedAmount} ${currency.toUpperCase()}`
	}

	const columns = [
		{
			title: t('single.plan'),
			dataIndex: ['items', 'data', 0, 'price', 'id'],
			key: 'plan',
			render: (priceId) => {
				const plan = plans.find((plan) => plan.id === priceId)
				if (!plan) {
					return priceId
				}
				const { name, description, metadata } = plan.product
				const planType = metadata?.type || 'N/A'
				const planCategory = metadata?.category || 'N/A'

				const billingFrequency = `${plan.recurring.interval_count} ${plan.recurring.interval}(s)`
				const currency = plan.currency.toUpperCase()
				const amount = `$${(plan.unit_amount / 100).toFixed(
					2
				)} ${currency}`

				return (
					<Tooltip
						title={
							<div>
								<p>
									<strong>{t('single.type')}</strong>{' '}
									{planType}
								</p>
								<p>
									<strong>{t('single.category')}</strong>{' '}
									{planCategory}
								</p>
								<p>
									<strong>{t('single.description')}</strong>{' '}
									{description ||
										t('single.no-description-available')}
								</p>
								<p>
									<strong>
										{t('single.billing-frequency')}
									</strong>{' '}
									{billingFrequency}
								</p>
								<p>
									<strong>{t('single.value')}</strong>{' '}
									{amount}
								</p>
							</div>
						}
					>
						<span>
							<InfoCircleOutlined style={{ marginRight: 5 }} />
							{name}
						</span>
					</Tooltip>
				)
			}
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status) => (
				<Tag color={status === 'active' ? 'green' : 'default'}>
					{status}
				</Tag>
			)
		},
		{
			title: t('single.value'),
			dataIndex: ['items', 'data', 0, 'price', 'unit_amount'],
			key: 'amount',
			render: (amount, record) =>
				formatPrice(amount, record.items.data[0].price.currency)
		},
		{
			title: t('single.dates'),
			dataIndex: 'status',
			key: 'dates',
			render: (status, record) => {
				if (status === 'canceled') {
					return `${t('single.canceled-on')}: ${new Date(
						record.canceled_at * 1000
					).toLocaleDateString()}`
				} else if (status === 'active') {
					return `${t('single.start')}: ${new Date(
						record.current_period_start * 1000
					).toLocaleDateString()} - End: ${new Date(
						record.current_period_end * 1000
					).toLocaleDateString()}`
				} else if (status === 'trialing') {
					return `${t('single.trial-ends-on')}: ${new Date(
						record.trial_end * 1000
					).toLocaleDateString()}`
				}
				return 'N/A'
			}
		},
		{
			title: t('_action.multiple'),
			dataIndex: ['id'],
			key: 'action',
			render: (id, { status, cancel_at_period_end }) => {
				return status === 'active' ? (
					cancel_at_period_end ? (
						<SubscriptionActions
							action="resume"
							subId={id}
							status={status}
							cancelAtPeriodEnd={cancel_at_period_end}
						/>
					) : (
						<SubscriptionActions
							action="cancel"
							subId={id}
							status={status}
							cancelAtPeriodEnd={cancel_at_period_end}
						/>
					)
				) : null
			}
		}
	]

	return (
		<>
			<Select
				style={{ width: 200, marginBottom: 20 }}
				placeholder={t('__filter-by-x%single.status')}
				options={statusOptions}
				value={statusFilter}
				onChange={(value) => setStatusFilter(value)}
			/>

			{filteredSubscriptions?.length ? (
				<Table
					columns={columns}
					dataSource={filteredSubscriptions}
					rowKey="id"
					pagination={false}
					scroll={{ x: isMobile && 'max-content' }} // TODO: max-content does not exist
				/>
			) : (
				<Title level={4}>
					<ExclamationCircleOutlined />{' '}
					{t('single.no-active-subscriptions')}
				</Title>
			)}
		</>
	)
}

export default observer(ActiveSubscriptions)
